/**
 * A retomada da saída que falhou (E168.F7, GUS-180): o motivo, de 15 a 500 caracteres, registrado
 * em nome de quem pede. Só a saída que o servidor marcou como retomável abre este formulário.
 */
import { useState, type FormEvent } from 'react';

import { Button, Field, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  JUSTIFICATIVA_MAXIMA,
  JUSTIFICATIVA_MINIMA,
  justificativaValida,
  type RecusaDaRetomada,
  type SaidaDoPacote,
} from '@/lib/preparacao_do_pacote';
import type { DesfechoDaRetomada } from '@/lib/usePreparacaoDoPacote';

const t = ptBR.preparacaoDoPacote;

function comLimites(texto: string): string {
  return texto
    .replace('{min}', String(JUSTIFICATIVA_MINIMA))
    .replace('{max}', String(JUSTIFICATIVA_MAXIMA));
}

export interface RetomadaDaSaidaProps {
  saida: SaidaDoPacote;
  onRetomar: (justificativa: string) => Promise<DesfechoDaRetomada>;
  onCancelar: () => void;
}

export function RetomadaDaSaida({
  saida,
  onRetomar,
  onCancelar,
}: RetomadaDaSaidaProps): JSX.Element {
  const [texto, setTexto] = useState('');
  const [tentou, setTentou] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [recusa, setRecusa] = useState<RecusaDaRetomada | null>(null);
  const invalida = tentou && !justificativaValida(texto);

  async function enviar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setTentou(true);
    if (!justificativaValida(texto) || enviando) return;
    setEnviando(true);
    setRecusa(null);
    const desfecho = await onRetomar(texto.trim());
    setEnviando(false);
    if (!desfecho.aceita) setRecusa(desfecho.recusa);
  }

  return (
    <form
      className="mt-sc-2 space-y-sc-2"
      data-testid={`pacote-retomada-${saida}`}
      onSubmit={(e) => void enviar(e)}
      noValidate
    >
      <Field
        label={t.formulario.rotulo}
        description={comLimites(t.formulario.dica)}
        error={invalida ? comLimites(t.recusa.justificativa) : undefined}
        required
      >
        <Textarea
          value={texto}
          rows={2}
          maxLength={JUSTIFICATIVA_MAXIMA}
          data-campo="justificativa"
          onChange={(e) => setTexto(e.target.value)}
        />
      </Field>
      {recusa !== null ? (
        <p
          role="alert"
          className="text-body-sm text-danger-700"
          data-testid="pacote-retomada-recusa"
        >
          {comLimites(t.recusa[recusa])}
        </p>
      ) : null}
      <div className="flex gap-sc-2">
        <Button type="submit" size="sm" loading={enviando}>
          {enviando ? t.formulario.enviando : t.formulario.enviar}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onCancelar}
          disabled={enviando}
        >
          {t.formulario.cancelar}
        </Button>
      </div>
    </form>
  );
}
